"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LiquityStore = void 0;
const assert_1 = __importDefault(require("assert"));
const strictEquals = (a, b) => a === b;
const eq = (a, b) => a.eq(b);
function compareMaps(map1, map2) {
    let testVal;
    if (map1.size !== map2.size) {
        return false;
    }
    for (const key in map1) {
        testVal = map2[key];
        // in cases of an undefined value, make sure the key
        // actually exists on the object so there are no false positives
        if (testVal.hex !== map1[key].hex || (testVal === undefined && !map2[key])) {
            return false;
        }
    }
    return true;
}
function compareMaps1(map1, map2) {
    let testVal;
    if (map1.size !== map2.size) {
        return false;
    }
    for (const key in map1) {
        testVal = map2[key];
        // in cases of an undefined value, make sure the key
        // actually exists on the object so there are no false positives
        if (testVal !== map1[key] || (testVal === undefined && !map2[key])) {
            return false;
        }
    }
    return true;
}
function compareArrays(arr1, arr2) {
    if (arr1.length !== arr2.length) {
        return false;
    }
    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
            return false;
        }
    }
    return true;
}
const equals = (a, b) => a.equals(b);
// const frontendStatusEquals = (a: FrontendStatus, b: FrontendStatus) =>
//   a.status === "unregistered"
//     ? b.status === "unregistered"
//     : b.status === "registered" && a.kickbackRate.eq(b.kickbackRate);
// const showFrontendStatus = (x: FrontendStatus) =>
//   x.status === "unregistered"
//     ? '{ status: "unregistered" }'
//     : `{ status: "registered", kickbackRate: ${x.kickbackRate} }`;
const wrap = (f) => (...args) => f(...args);
const difference = (a, b) => Object.fromEntries(Object.entries(a).filter(([key, value]) => value !== b[key]));
/**
 * Abstract base class of Liquity data store implementations.
 *
 * @remarks
 * The type parameter `T` may be used to type extra state added to {@link LiquityStoreState} by the
 * subclass.
 *
 * Implemented by {@link @yeti/lib-ethers#BlockPolledLiquityStore}.
 *
 * @public
 */
class LiquityStore {
    constructor() {
        /** Turn console logging on/off. */
        this.logging = false;
        /** @internal */
        this._loaded = false;
        this._listeners = new Set();
    }
    /**
     * The current store state.
     *
     * @remarks
     * Should not be accessed before the store is loaded. Assign a function to
     * {@link LiquityStore.onLoaded | onLoaded} to get a callback when this happens.
     *
     * See {@link LiquityStoreState} for the list of properties returned.
     */
    get state() {
        return Object.assign({}, this._baseState, this._derivedState, this._extraState);
    }
    /**
     * Start monitoring the blockchain for Liquity state changes.
     *
     * @remarks
     * The {@link LiquityStore.onLoaded | onLoaded} callback will be called after the state is fetched
     * for the first time.
     *
     * Use the {@link LiquityStore.subscribe | subscribe()} function to register listeners.
     *
     * @returns Function to stop the monitoring.
     */
    start() {
        const doStop = this._doStart();
        return () => {
            doStop();
            this._cancelUpdateIfScheduled();
        };
    }
    _cancelUpdateIfScheduled() {
        if (this._updateTimeoutId !== undefined) {
            clearTimeout(this._updateTimeoutId);
        }
    }
    _scheduleUpdate() {
        this._cancelUpdateIfScheduled();
        this._updateTimeoutId = setTimeout(() => {
            this._updateTimeoutId = undefined;
            this._update();
        }, 30000);
    }
    _logUpdate(name, next, show) {
        if (this.logging) {
            // console.log(`${name} updated to ${show ? show(next) : next}`);
        }
        return next;
    }
    _updateIfChanged(equals, name, prev, next, show) {
        return next !== undefined && !equals(prev, next) ? this._logUpdate(name, next, show) : prev;
    }
    _silentlyUpdateIfChanged(equals, prev, next) {
        return next !== undefined && !equals(prev, next) ? next : prev;
    }
    _updateFees(name, prev, next) {
        if (next && !next.equals(prev)) {
            // Filter out fee update spam that happens on every new block by only logging when string
            // representation changes.
            if (`${next}` !== `${prev}`) {
                this._logUpdate(name, next);
            }
            return next;
        }
        else {
            return prev;
        }
    }
    _reduce(baseState, baseStateUpdate) {
        return {
            numberOfTroves: this._updateIfChanged(strictEquals, "numberOfTroves", baseState.numberOfTroves, baseStateUpdate.numberOfTroves),
            pendingYUSDInterest: this._updateIfChanged(eq, "pendingYUSDInterest", baseState.pendingYUSDInterest, baseStateUpdate.pendingYUSDInterest),
            borrowingRate: this._updateIfChanged(eq, "borrowingRate", baseState.borrowingRate, baseStateUpdate.borrowingRate),
            accountBalance: this._updateIfChanged(eq, "accountBalance", baseState.accountBalance, baseStateUpdate.accountBalance),
            tokenBalances: this._updateIfChanged(compareMaps, "tokenBalance", baseState.tokenBalances, baseStateUpdate.tokenBalances),
            vaultDepositInfo: this._updateIfChanged(equals, "vaultDepositInfo", baseState.vaultDepositInfo, baseStateUpdate.vaultDepositInfo),
            lpTokenBalance: this._updateIfChanged(eq, "tokenBalance", baseState.lpTokenBalance, baseStateUpdate.lpTokenBalance),
            yusdBalance: this._updateIfChanged(eq, "yusdBalance", baseState.yusdBalance, baseStateUpdate.yusdBalance),
            yetiBalance: this._updateIfChanged(eq, "yetiBalance", baseState.yetiBalance, baseStateUpdate.yetiBalance),
            collateralSurplusBalance: this._updateIfChanged(compareMaps, "collateralSurplusBalance", baseState.collateralSurplusBalance, baseStateUpdate.collateralSurplusBalance),
            underlyingPrices: this._updateIfChanged(compareMaps, "underlyingPrices", baseState.underlyingPrices, baseStateUpdate.underlyingPrices),
            interestRates: this._updateIfChanged(compareMaps, "interestRates", baseState.interestRates, baseStateUpdate.interestRates),
            prices: this._updateIfChanged(compareMaps, "prices", baseState.prices, baseStateUpdate.prices),
            poolRewardRate: this._updateIfChanged(eq, "yusdInStabilityPool", baseState.poolRewardRate, baseStateUpdate.poolRewardRate),
            yusdInStabilityPool: this._updateIfChanged(eq, "yusdInStabilityPool", baseState.yusdInStabilityPool, baseStateUpdate.yusdInStabilityPool),
            total: this._updateIfChanged(equals, "total", baseState.total, baseStateUpdate.total),
            totalRedistributed: this._updateIfChanged(equals, "totalRedistributed", baseState.totalRedistributed, baseStateUpdate.totalRedistributed),
            troveBeforeRedistribution: this._updateIfChanged(equals, "troveBeforeRedistribution", baseState.troveBeforeRedistribution, baseStateUpdate.troveBeforeRedistribution),
            stabilityDeposit: this._updateIfChanged(equals, "stabilityDeposit", baseState.stabilityDeposit, baseStateUpdate.stabilityDeposit),
            farm: this._updateIfChanged(equals, "farm", baseState.farm, baseStateUpdate.farm),
            boostedFarm: this._updateIfChanged(equals, "boostedFarm", baseState.boostedFarm, baseStateUpdate.boostedFarm),
            veYETIStaked: this._updateIfChanged(equals, "farm", baseState.veYETIStaked, baseStateUpdate.veYETIStaked),
            _feesInNormalMode: this._silentlyUpdateIfChanged(equals, baseState._feesInNormalMode, baseStateUpdate._feesInNormalMode),
            whitelistedCollaterals: this._updateIfChanged(compareArrays, "whitelistedCollaterals", baseState.whitelistedCollaterals, baseStateUpdate.whitelistedCollaterals),
            vaultTokens: this._updateIfChanged(compareArrays, "vaultTokens", baseState.vaultTokens, baseStateUpdate.vaultTokens),
            underlyingTokens: this._updateIfChanged(compareArrays, "underlyingTokens", baseState.underlyingTokens, baseStateUpdate.underlyingTokens),
            icr: this._updateIfChanged(eq, "icr", baseState.icr, baseStateUpdate.icr),
            vcValue: this._updateIfChanged(eq, "vcValue", baseState.vcValue, baseStateUpdate.vcValue),
            safetyRatios: this._updateIfChanged(compareMaps, "safetyRatios", baseState.safetyRatios, baseStateUpdate.safetyRatios),
            recoveryRatios: this._updateIfChanged(compareMaps, "recoveryRatios", baseState.recoveryRatios, baseStateUpdate.recoveryRatios),
            decimals: this._updateIfChanged(compareMaps, "decimals", baseState.decimals, baseStateUpdate.decimals),
            YETIPrice: this._updateIfChanged(eq, "YETIPrice", baseState.YETIPrice, baseStateUpdate.YETIPrice),
            YUSDPrice: this._updateIfChanged(eq, "YUSDPrice", baseState.YUSDPrice, baseStateUpdate.YUSDPrice),
            globalBoostFactor: this._updateIfChanged(eq, "globalBoostFactor", baseState.globalBoostFactor, baseStateUpdate.globalBoostFactor),
            decayedBoost: this._updateIfChanged(eq, "decayedBoost", baseState.decayedBoost, baseStateUpdate.decayedBoost),
            receiptPerUnderlyingRatios: this._updateIfChanged(compareMaps, "receiptPerUnderlyingRatios", baseState.receiptPerUnderlyingRatios, baseStateUpdate.receiptPerUnderlyingRatios),
            underlyingPerReceiptRatios: this._updateIfChanged(compareMaps, "underlyingPerReceiptRatios", baseState.underlyingPerReceiptRatios, baseStateUpdate.underlyingPerReceiptRatios),
            underlyingDecimals: this._updateIfChanged(compareMaps, "underlyingDecimals", baseState.underlyingDecimals, baseStateUpdate.underlyingDecimals),
            PSMSwapFee: this._updateIfChanged(eq, "PSMSwapFee", baseState.PSMSwapFee, baseStateUpdate.PSMSwapFee),
            PSMYUSDContractDebt: this._updateIfChanged(eq, "PSMYUSDContractDebt", baseState.PSMYUSDContractDebt, baseStateUpdate.PSMYUSDContractDebt),
            PSMYUSDDebtLimit: this._updateIfChanged(eq, "PSMYUSDDebtLimit", baseState.PSMYUSDDebtLimit, baseStateUpdate.PSMYUSDDebtLimit),
            DollarCaps: this._updateIfChanged(compareMaps, "DollarCaps", baseState.DollarCaps, baseStateUpdate.DollarCaps),
            Oracles: this._updateIfChanged(compareMaps1, "Oracles", baseState.Oracles, baseStateUpdate.Oracles)
        };
    }
    _derive({ troveBeforeRedistribution, totalRedistributed, _feesInNormalMode, total, prices, recoveryRatios }) {
        const fees = _feesInNormalMode._setRecoveryMode(total.collateralRatioIsBelowCritical(prices, recoveryRatios));
        return {
            trove: troveBeforeRedistribution.applyRedistribution(totalRedistributed),
            fees,
            redemptionRate: fees.redemptionRate(),
        };
    }
    _reduceDerived(derivedState, derivedStateUpdate) {
        return {
            fees: this._updateFees("fees", derivedState.fees, derivedStateUpdate.fees),
            trove: this._updateIfChanged(equals, "trove", derivedState.trove, derivedStateUpdate.trove),
            redemptionRate: this._silentlyUpdateIfChanged(eq, derivedState.redemptionRate, derivedStateUpdate.redemptionRate),
        };
    }
    _notify(params) {
        // Iterate on a copy of `_listeners`, to avoid notifying any new listeners subscribed by
        // existing listeners, as that could result in infinite loops.
        //
        // Before calling a listener from our copy of `_listeners`, check if it has been removed from
        // the original set. This way we avoid calling listeners that have already been unsubscribed
        // by an earlier listener callback.
        [...this._listeners].forEach(listener => {
            if (this._listeners.has(listener)) {
                listener(params);
            }
        });
    }
    /**
     * Register a state change listener.
     *
     * @param listener - Function that will be called whenever state changes.
     * @returns Function to unregister this listener.
     */
    subscribe(listener) {
        const uniqueListener = wrap(listener);
        this._listeners.add(uniqueListener);
        return () => {
            this._listeners.delete(uniqueListener);
        };
    }
    /** @internal */
    _load(baseState, extraState) {
        assert_1.default(!this._loaded);
        this._baseState = baseState;
        this._derivedState = this._derive(baseState);
        this._extraState = extraState;
        this._loaded = true;
        this._scheduleUpdate();
        if (this.onLoaded) {
            this.onLoaded();
        }
    }
    /** @internal */
    _update(baseStateUpdate, extraStateUpdate) {
        assert_1.default(this._baseState && this._derivedState);
        const oldState = this.state;
        if (baseStateUpdate) {
            // console.log("UPDATE 5")
            this._baseState = this._reduce(this._baseState, baseStateUpdate);
            // console.log("UPDATE 2")
        }
        // Always running this lets us derive state based on passage of time, like baseRate decay
        this._derivedState = this._reduceDerived(this._derivedState, this._derive(this._baseState));
        if (extraStateUpdate) {
            assert_1.default(this._extraState);
            this._extraState = this._reduceExtra(this._extraState, extraStateUpdate);
        }
        this._scheduleUpdate();
        this._notify({
            newState: this.state,
            oldState,
            stateChange: difference(this.state, oldState)
        });
        // console.log("UPDATE final")
    }
}
exports.LiquityStore = LiquityStore;
//# sourceMappingURL=LiquityStore.js.map